import { useMemo } from "react";
import { types } from "@vilocnv/allsetra-core";
import { getAddressDisplayInfo } from "app/data/helpers";

/**
 * Hook to validate if a ride has hidden addresses
 * A ride is considered to have hidden address when the backend doesn't return coordinates
 * (startLatitude, startLongitude, endLatitude, endLongitude are null/undefined)
 *
 * This is used to determine:
 * - If the ride can be viewed on map
 * - If the map should show private ride styles
 * - If markers and interactive elements should be hidden
 */
export const useRideAddressValidation = (ride: types.IObjectRide | null) => {
  const validation = useMemo(() => {
    if (!ride) {
      return {
        hasHiddenAddress: false,
        canViewOnMap: false,
        startAddressInfo: null,
        endAddressInfo: null
      };
    }

    const startAddressInfo = getAddressDisplayInfo(
      ride.startLatitude,
      ride.startLongitude,
      null
    );

    const endAddressInfo = getAddressDisplayInfo(
      ride.endLatitude,
      ride.endLongitude,
      null
    );

    // A ride has hidden address if BOTH start and end addresses are hidden
    const hasHiddenAddress =
      startAddressInfo.isHidden && endAddressInfo.isHidden;

    // Can view on map if at least one address is visible
    const canViewOnMap = !startAddressInfo.isHidden || !endAddressInfo.isHidden;

    return {
      hasHiddenAddress,
      canViewOnMap,
      startAddressInfo,
      endAddressInfo
    };
  }, [ride]);

  return validation;
};

/**
 * Utility function to check if a ride can be viewed on map
 * Can be used outside of React components
 */
export const canViewRideOnMap = (ride: types.IObjectRide): boolean => {
  const startAddressInfo = getAddressDisplayInfo(
    ride.startLatitude,
    ride.startLongitude,
    null
  );

  const endAddressInfo = getAddressDisplayInfo(
    ride.endLatitude,
    ride.endLongitude,
    null
  );

  return !startAddressInfo.isHidden || !endAddressInfo.isHidden;
};

/**
 * Utility function to check if a ride has hidden address
 * Can be used outside of React components
 */
export const hasRideHiddenAddress = (ride: types.IObjectRide): boolean => {
  const startAddressInfo = getAddressDisplayInfo(
    ride.startLatitude,
    ride.startLongitude,
    null
  );

  const endAddressInfo = getAddressDisplayInfo(
    ride.endLatitude,
    ride.endLongitude,
    null
  );

  return startAddressInfo.isHidden && endAddressInfo.isHidden;
};
